// frontend/components/notifications/AdminNotificationBell.tsx

"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSession } from "next-auth/react";
import { Bell, RefreshCw, X, ChevronLeft, CheckCheck, UserCog, KeyRound } from "lucide-react";
import { API_URL, authHeaders } from "../../utils/api";
import { ALL_DEPARTMENTS, POSITIONS } from "../../constants/organization";
import { formatRelativeTime } from "../../utils/dateUtils";

interface User {
  id: string;
  loginId: string;
  name: string | null;
  department: string | null;
  position: string | null;
  isDeptHead: boolean;
  role: string;
}

interface ChangeRequest {
  id: string;
  userId: string;
  type: "PROFILE_CHANGE" | "PASSWORD_RESET";
  department: string | null;
  position: string | null;
  isDeptHead: boolean | null;
  status: "PENDING" | "DONE";
  createdAt: string;
  user: User;
}

interface DetailState {
  request: ChangeRequest;
  editDept: string;
  editPosition: string;
  editIsDeptHead: boolean;
  tempPass: string | null;
  loading: boolean;
  error: string;
}

// 서버(/api/notifications) 응답 스키마. 예전의 휘발성 로컬 구조를 대체한다.
interface LocalNotif {
  id: string;
  message: string;
  url?: string | null;
  createdAt: string;
  isRead: boolean;
}

const timeAgo = (iso: string) => formatRelativeTime(iso);

export default function AdminNotificationBell() {
  const { data: session } = useSession();
  const user = session?.user as any;
  const isAdmin = user?.role === "ADMIN";

  const [open, setOpen] = useState(false);
  const [requests, setRequests] = useState<ChangeRequest[]>([]);
  const [detail, setDetail] = useState<DetailState | null>(null);
  const [localNotifications, setLocalNotifications] = useState<LocalNotif[]>([]);
  const [activeTab, setActiveTab] = useState<"notifications" | "requests">("notifications");
  const panelRef = useRef<HTMLDivElement>(null);

  const accessToken = user?.accessToken as string | undefined;

  // 1. 요청 내역 조회 (Admin = 전체 PENDING 목록, User = 본인 신청 내역)
  const fetchRequests = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await fetch(`${API_URL}/system/requests`, { headers: authHeaders(accessToken) });
      if (res.ok) setRequests(await res.json());
    } catch {}
  }, [accessToken]);

  useEffect(() => {
    if (!user) return;
    fetchRequests();
    const id = setInterval(fetchRequests, 60_000);
    return () => clearInterval(id);
  }, [user, fetchRequests]);

  // 2. 안 읽은 알림 조회
  //
  // 예전에는 `show-toast` 이벤트를 useState 에 모으기만 했다.
  // 즉 **새로고침하면 전부 사라지는 휘발성 목록**이었고, 사이드바 알림함(서버 영속)과
  // 같은 알림이 서로 다르게 보였다. 이제 둘 다 서버(/api/notifications)를 본다.
  const fetchUnread = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await fetch(`${API_URL}/notifications?unreadOnly=1&limit=20`, {
        headers: authHeaders(accessToken),
      });
      if (res.ok) setLocalNotifications(await res.json());
    } catch { /* 배지·목록이라 실패해도 조용히 넘어간다 */ }
  }, [accessToken]);

  useEffect(() => {
    if (!user) return;
    fetchUnread();
    // 서버가 알림을 적재하면 신호를 보낸다(사이드바 알림함과 동일한 경로). 폴링하지 않는다.
    const onInbox = () => fetchUnread();
    window.addEventListener("notification-inbox", onInbox);
    return () => window.removeEventListener("notification-inbox", onInbox);
  }, [user, fetchUnread]);

  // 패널 외부 클릭 시 닫기
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
        setDetail(null);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  // 권한/세션 정보가 없으면 아무것도 렌더링하지 않음
  if (!user) return null;

  // 미해결 알림 개수 계산
  const unreadNotifCount = localNotifications.length; // 서버에서 unreadOnly=1 로 받아온다
  // 관리자는 pending 중인 전체 요청 개수, 사용자는 본인의 pending 상태 요청 개수를 계산
  const pendingRequestCount = requests.filter(r => r.status === "PENDING").length;
  const count = isAdmin ? pendingRequestCount : unreadNotifCount + pendingRequestCount;

  // 평소에는 화면에서 사라져 있고, **확인이 필요한 것이 생겼을 때만** 나타난다.
  // 알림함의 정본은 사이드바(NotificationInbox)이고, 이 플로팅 버튼은
  // 사이드바를 접어 둔 상태에서도 놓치지 않게 하는 보조 진입점 역할이다.
  // (열려 있는 동안에는 처리 도중 버튼이 사라지지 않도록 유지한다)
  if (count === 0 && !open) return null;

  const openDetail = (req: ChangeRequest) => {
    setDetail({
      request: req,
      editDept: req.department ?? req.user.department ?? "",
      editPosition: req.position ?? req.user.position ?? "",
      editIsDeptHead: req.isDeptHead ?? req.user.isDeptHead,
      tempPass: null,
      loading: false,
      error: "",
    });
  };

  const markDone = async (id: string) => {
    await fetch(`${API_URL}/system/requests/${id}/done`, {
      method: "PATCH",
      headers: authHeaders(accessToken),
    }).catch(() => {});
    setRequests(prev => prev.filter(r => r.id !== id));
    setDetail(null);
  };

  const applyProfileChange = async () => {
    if (!detail) return;
    setDetail(d => d && ({ ...d, loading: true, error: "" }));
    try {
      const res = await fetch(`${API_URL}/auth/members/${detail.request.user.id}`, {
        method: "PATCH",
        headers: authHeaders(accessToken),
        body: JSON.stringify({
          department: detail.editDept || null,
          position: detail.editPosition || null,
          isDeptHead: detail.editIsDeptHead,
        }),
      });
      if (!res.ok) throw new Error("수정에 실패했습니다.");
      await markDone(detail.request.id);
    } catch (e: any) {
      setDetail(d => d && ({ ...d, loading: false, error: e.message }));
    }
  };

  const handlePasswordReset = async () => {
    if (!detail) return;
    setDetail(d => d && ({ ...d, loading: true, error: "" }));
    try {
      const res = await fetch(`${API_URL}/auth/reset-password-admin`, {
        method: "POST",
        headers: authHeaders(accessToken),
        body: JSON.stringify({ targetLoginId: detail.request.user.loginId }),
      });
      if (!res.ok) throw new Error("초기화에 실패했습니다.");
      const data = await res.json();
      setDetail(d => d && ({ ...d, loading: false, tempPass: data.tempPassword }));
      await fetch(`${API_URL}/system/requests/${detail.request.id}/done`, {
        method: "PATCH",
        headers: authHeaders(accessToken),
      }).catch(() => {});
      setRequests(prev => prev.filter(r => r.id !== detail.request.id));
    } catch (e: any) {
      setDetail(d => d && ({ ...d, loading: false, error: e.message }));
    }
  };

  const labelCls = "block text-[10px] font-bold text-gray-400 dark:text-zinc-500 mb-1";
  const inputCls = "w-full px-2 py-1 text-xs border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-gray-800 dark:text-zinc-100 rounded focus:outline-none focus:border-blue-500";

  return (
    <div ref={panelRef} className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2 print:hidden">

      {/* 알림 패널 */}
      {open && (
        <div className="w-80 max-h-[70vh] flex flex-col bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-2 fade-in duration-200">

          {/* 패널 헤더 */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-zinc-800 shrink-0">
            {detail ? (
              <button
                onClick={() => setDetail(null)}
                className="flex items-center gap-1 text-xs text-gray-500 dark:text-zinc-400 hover:text-gray-800 dark:hover:text-zinc-100 transition-colors"
              >
                <ChevronLeft size={14} />목록으로
              </button>
            ) : (
              <span className="text-sm font-bold text-gray-800 dark:text-zinc-100">알림 내역</span>
            )}
            <button
              onClick={() => { setOpen(false); setDetail(null); }}
              className="p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded transition-colors"
            >
              <X size={15} />
            </button>
          </div>

          {/* 탭 인터페이스 (일반 사용자용 - 알림 vs 요청 내역 분리) */}
          {!detail && !isAdmin && (
            <div className="flex border-b border-gray-100 dark:border-zinc-800 shrink-0 text-xs text-gray-500 dark:text-zinc-400">
              <button
                onClick={() => setActiveTab("notifications")}
                className={`flex-1 py-2 font-semibold border-b-2 transition-colors ${
                  activeTab === "notifications"
                    ? "border-[#006DBA] text-[#006DBA] dark:text-blue-400 dark:border-blue-400"
                    : "border-transparent hover:text-gray-800 dark:hover:text-zinc-200"
                }`}
              >
                알림 ({localNotifications.length})
              </button>
              <button
                onClick={() => setActiveTab("requests")}
                className={`flex-1 py-2 font-semibold border-b-2 transition-colors ${
                  activeTab === "requests"
                    ? "border-[#006DBA] text-[#006DBA] dark:text-blue-400 dark:border-blue-400"
                    : "border-transparent hover:text-gray-800 dark:hover:text-zinc-200"
                }`}
              >
                요청 진행 ({requests.length})
              </button>
            </div>
          )}

          {/* 목록 및 상세 영역 */}
          <div className="flex-1 overflow-y-auto">

            {/* 상세 뷰 */}
            {detail && (
              isAdmin ? (
                <div className="p-4 space-y-4">
                  {/* 사용자 카드 */}
                  <div className="p-3 bg-gray-50 dark:bg-zinc-800/40 rounded-lg border border-gray-100 dark:border-zinc-800 text-xs">
                    <p className="font-mono font-medium text-[#006DBA] dark:text-[#3399FF]">{detail.request.user.loginId}</p>
                    <p className="font-bold text-sm text-gray-800 dark:text-zinc-100 mt-0.5">{detail.request.user.name || "-"}</p>
                    <p className="text-gray-400 dark:text-zinc-500 mt-0.5">
                      {[detail.request.user.department, detail.request.user.position, detail.request.user.isDeptHead ? "부서장" : null].filter(Boolean).join(" · ") || "미설정"}
                    </p>
                  </div>

                  {/* PROFILE_CHANGE 편집 폼 */}
                  {detail.request.type === "PROFILE_CHANGE" && (
                    <>
                      <div className="text-xs font-medium text-gray-500 dark:text-zinc-400">변경 요청 내용</div>
                      <div className="space-y-2">
                        <div>
                          <label className={labelCls}>소속</label>
                          <select
                            value={detail.editDept}
                            onChange={e => setDetail(d => d && ({ ...d, editDept: e.target.value }))}
                            className={inputCls}
                          >
                            <option value="">선택 안 함</option>
                            {ALL_DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={labelCls}>직책</label>
                          <div className="flex items-center gap-2">
                            <select
                              value={detail.editPosition}
                              onChange={e => setDetail(d => d && ({ ...d, editPosition: e.target.value }))}
                              className={`${inputCls} flex-1`}
                            >
                              <option value="">선택 안 함</option>
                              {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                            <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-zinc-300 whitespace-nowrap cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={detail.editIsDeptHead}
                                onChange={e => setDetail(d => d && ({ ...d, editIsDeptHead: e.target.checked }))}
                                className="w-3 h-3 accent-blue-500"
                              />
                              부서장
                            </label>
                          </div>
                        </div>
                      </div>

                      {detail.error && <p className="text-xs text-red-500">{detail.error}</p>}

                      <div className="flex gap-2">
                        <button
                          onClick={applyProfileChange}
                          disabled={detail.loading}
                          className="flex-1 py-1.5 bg-[#006DBA] hover:bg-[#005A9E] disabled:opacity-60 text-white text-xs font-medium rounded-lg transition-colors"
                        >
                          {detail.loading ? "적용 중…" : "적용하기"}
                        </button>
                        <button
                          onClick={() => markDone(detail.request.id)}
                          className="px-3 py-1.5 border border-gray-200 dark:border-zinc-700 text-xs text-gray-500 dark:text-zinc-400 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors"
                        >
                          무시
                        </button>
                      </div>
                    </>
                  )}

                  {/* PASSWORD_RESET */}
                  {detail.request.type === "PASSWORD_RESET" && (
                    <>
                      <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/60 rounded-lg text-xs text-amber-700 dark:text-amber-400">
                        비밀번호 초기화를 요청했습니다.
                      </div>

                      {detail.tempPass ? (
                        <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800/60 rounded-lg text-xs space-y-1">
                          <p className="font-bold text-green-700 dark:text-green-400">초기화 완료</p>
                          <p className="text-gray-600 dark:text-zinc-300">임시 비밀번호:</p>
                          <p className="font-mono font-bold text-green-700 dark:text-green-300 bg-white dark:bg-black/20 px-2 py-1 rounded border border-green-200 dark:border-green-800">
                            {detail.tempPass}
                          </p>
                          <p className="text-gray-400 dark:text-zinc-500">팀원에게 직접 전달해주세요.</p>
                        </div>
                      ) : (
                        <>
                          {detail.error && <p className="text-xs text-red-500">{detail.error}</p>}
                          <div className="flex gap-2">
                            <button
                              onClick={handlePasswordReset}
                              disabled={detail.loading}
                              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-xs font-medium rounded-lg transition-colors"
                            >
                              <KeyRound size={12} />
                              {detail.loading ? "초기화 중…" : "비밀번호 초기화"}
                            </button>
                            <button
                              onClick={() => markDone(detail.request.id)}
                              className="px-3 py-1.5 border border-gray-200 dark:border-zinc-700 text-xs text-gray-500 dark:text-zinc-400 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors"
                            >
                              무시
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              ) : (
                // 일반 사용자용 요청 상세 내역 조회
                <div className="p-4 space-y-4 text-xs">
                  <div className="p-3 bg-gray-50 dark:bg-zinc-800 border border-gray-100 dark:border-zinc-800 rounded-lg space-y-1.5">
                    <p className="font-bold text-gray-800 dark:text-zinc-100">
                      {detail.request.type === "PROFILE_CHANGE" ? "소속·직책 변경 요청" : "비밀번호 초기화 요청"}
                    </p>
                    <p className="text-gray-400 dark:text-zinc-500 mt-1">
                      신청일: {new Date(detail.request.createdAt).toLocaleString("ko-KR")}
                    </p>
                    <p className="text-gray-400 dark:text-zinc-500">
                      상태:{" "}
                      <span
                        className={`font-bold ${
                          detail.request.status === "PENDING"
                            ? "text-amber-500 dark:text-amber-400"
                            : "text-green-600 dark:text-green-400"
                        }`}
                      >
                        {detail.request.status === "PENDING" ? "승인 대기 중" : "승인/처리 완료"}
                      </span>
                    </p>
                  </div>
                  {detail.request.type === "PROFILE_CHANGE" && (
                    <div className="space-y-1 text-gray-600 dark:text-zinc-300">
                      <p className="font-semibold">요청한 정보:</p>
                      <ul className="list-disc pl-4 space-y-0.5">
                        {detail.request.department && <li>소속 부서: {detail.request.department}</li>}
                        {detail.request.position && <li>직책: {detail.request.position}</li>}
                        {detail.request.isDeptHead !== null && <li>부서장 직 부여: {detail.request.isDeptHead ? "예" : "아니오"}</li>}
                      </ul>
                    </div>
                  )}
                  <button
                    onClick={() => setDetail(null)}
                    className="w-full py-1.5 border border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-800 text-gray-500 dark:text-zinc-400 rounded-lg transition-colors font-medium"
                  >
                    목록으로 돌아가기
                  </button>
                </div>
              )
            )}

            {/* 목록 뷰 */}
            {!detail && (
              <>
                {/* 1. 실시간 알림 탭 (USER 전용) */}
                {!isAdmin && activeTab === "notifications" && (
                  localNotifications.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-8 text-center text-gray-400 dark:text-zinc-500">
                      <CheckCheck size={28} className="opacity-40" />
                      <p className="text-xs">새로운 알림이 없습니다.</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
                      {localNotifications.map(notif => (
                        <li
                          key={notif.id}
                          onClick={async () => {
                            // 서버에 읽음 처리까지 반영한다(예전엔 화면에서만 지웠다).
                            setLocalNotifications(prev => prev.filter(n => n.id !== notif.id));
                            try {
                              await fetch(`${API_URL}/notifications/${notif.id}/read`, {
                                method: "PATCH",
                                headers: authHeaders(accessToken),
                              });
                            } catch { fetchUnread(); }
                            window.dispatchEvent(new Event("notification-inbox")); // 사이드바 배지도 갱신
                            if (notif.url) window.location.href = notif.url;
                          }}
                          className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-zinc-800/50 cursor-pointer transition-colors text-xs border-b border-gray-100 dark:border-zinc-800"
                        >
                          <div className="mt-0.5 w-7 h-7 rounded-full bg-blue-50 dark:bg-blue-900/20 text-[#006DBA] dark:text-blue-400 flex items-center justify-center shrink-0">
                            <Bell size={13} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-700 dark:text-zinc-200 leading-normal">{notif.message}</p>
                            <p className="text-[10px] text-gray-400 dark:text-zinc-500 mt-1">{timeAgo(notif.createdAt)}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )
                )}

                {/* 2. 요청 내역 탭 (ADMIN은 default 노출, USER는 requests 탭 노출) */}
                {(isAdmin || activeTab === "requests") && (
                  requests.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-8 text-center text-gray-400 dark:text-zinc-500">
                      <CheckCheck size={28} className="opacity-40" />
                      <p className="text-xs">{isAdmin ? "처리할 알림이 없습니다." : "진행 중인 요청이 없습니다."}</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
                      {requests.map(req => (
                        <li
                          key={req.id}
                          onClick={() => openDetail(req)}
                          className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-zinc-800/50 cursor-pointer transition-colors"
                        >
                          <div className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                            req.type === "PROFILE_CHANGE"
                              ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                              : "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"
                          }`}>
                            {req.type === "PROFILE_CHANGE" ? <UserCog size={13} /> : <KeyRound size={13} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-800 dark:text-zinc-100 truncate">
                              {req.user.name || req.user.loginId}
                            </p>
                            <p className="text-[11px] text-gray-400 dark:text-zinc-500 mt-0.5 flex items-center gap-1.5">
                              <span>
                                {req.type === "PROFILE_CHANGE"
                                  ? `직급·소속 변경 요청`
                                  : `비밀번호 초기화 요청`}
                              </span>
                              {!isAdmin && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                                  req.status === "PENDING"
                                    ? "bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400"
                                    : "bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400"
                                }`}>
                                  {req.status === "PENDING" ? "대기중" : "완료"}
                                </span>
                              )}
                            </p>
                          </div>
                          <span className="text-[10px] text-gray-400 dark:text-zinc-500 shrink-0 mt-0.5">{timeAgo(req.createdAt)}</span>
                        </li>
                      ))}
                    </ul>
                  )
                )}
              </>
            )}
          </div>

          {/* 새로고침 푸터 */}
          {!detail && (isAdmin || activeTab === "requests") && requests.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-100 dark:border-zinc-800 shrink-0">
              <button
                onClick={fetchRequests}
                className="w-full flex items-center justify-center gap-1 text-[11px] text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300 transition-colors py-1"
              >
                <RefreshCw size={11} />새로고침
              </button>
            </div>
          )}
        </div>
      )}

      {/* 종 버튼 */}
      <button
        onClick={() => { setOpen(v => !v); if (!open) setDetail(null); }}
        className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center relative transition-all ${
          open
            ? "bg-[#006DBA] text-white shadow-blue-500/30 scale-105"
            : "bg-white dark:bg-zinc-800 text-gray-600 dark:text-zinc-300 border border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700 hover:scale-105"
        }`}
      >
        <Bell size={18} />
        {count > 0 && !open && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full leading-none">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

    </div>
  );
}
